import React, { useEffect, useState } from "react";
import DatePickerStep from "./dayPicker";
import FormStep from "./form";
import { RequestFormData, ICreateTimeOffRequest, IUpdateTimeoffRequest } from "../../../../types";
import { RootState, useAppDispatch } from "../../../../redux/store";
import {
    clearTimeoffToUpdate,
    clearTimeoffUpdateMode,
    setRequestEndDate,
    setRequestStartDate,
    setTimeoffUpdateMode,
} from "../../../../redux/features/timeoff/slice";
import { getAssignedTimeoffCategories } from "../../../../redux/features/common";
import { createTimeOffRequest, updateTimeoffRequest } from "../../../../redux/features/timeoff";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { setShowTopNav } from "../../../../redux/features/mobileLayout";
import { DateTimeUtil } from "../../../../utils";
import { toast } from "react-toastify";
import { clearGlobalBackHandler, setGlobalBackHandler } from "../../../../hooks";
import { useNavigationHistory } from "../../../../hooks/useNavigationHistory";

const TimeOffRequest: React.FC = () => {
    const { fetchedTimeoffRequests, isUpdateTimeoffMode, timeoffToUpdate } = useSelector(
        (state: RootState) => state.timeoff
    );
    const { setSourcePath } = useNavigationHistory();
    const [step, setStep] = useState<number>(1);
    const [selectedDates, setSelectedDates] = useState<Date[]>([]);
    const [formData, setFormData] = useState<RequestFormData>({
        category_code: isUpdateTimeoffMode ? timeoffToUpdate?.category_code || "" : "",
        note: isUpdateTimeoffMode ? timeoffToUpdate?.reason || "" : "",
        details: [],
    });

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(getAssignedTimeoffCategories());
    }, [dispatch]);

    /**
     * Handle navigation to the previous step
     * - If on step 1, navigate back to the time off screen
     * - If on a later step, go back to the previous step
     */
    const handlePreviousStep = () => {
        if (step === 1) {
            navigate("/m-timeoff");
        } else {
            setStep((prev) => prev - 1);
            dispatch(setShowTopNav(true));
        }
    };

    // Set up navigation history and source path on mount
    useEffect(() => {
        // Set the source path for back navigation to be the timeoff screen
        setSourcePath("/m-timeoff");
        
        // Set up global back handler that checks current step 
        setGlobalBackHandler(() => {
            // Check current step when handler is called instead of when effect runs
            if (step === 1) {
                navigate("/m-timeoff");
            } else {
                setStep((prev) => prev - 1);
                dispatch(setShowTopNav(true));
            }
            return true;
        });
    }, [navigate, setSourcePath, step, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(clearTimeoffUpdateMode());
            dispatch(clearTimeoffToUpdate());
            clearGlobalBackHandler();
        };
    }, [dispatch]);

    useEffect(() => {
        if (selectedDates.length > 0) {
            const startDate = selectedDates[0];
            const endDate = selectedDates[selectedDates.length - 1];
            dispatch(setRequestStartDate(startDate.toString()));
            dispatch(setRequestEndDate(endDate.toString()));
        }
    }, [selectedDates]);

    const handleNextStep = () => {
        const hasConflict = selectedDates.some((selectedDate) =>
            fetchedTimeoffRequests.some((request) => {
                const requestStartDate = new Date(request.start_date ?? "");
                const requestEndDate = new Date(request.end_date ?? "");
                return selectedDate >= requestStartDate && selectedDate <= requestEndDate;
            })
        );

        if (hasConflict) {
            toast.error("You already have a time-off request for one or more selected dates.", {
                position: "top-center",
                autoClose: 3000,
            });
            return;
        } else {
            setStep((prev) => prev + 1);
            // dispatch(setShowTopNav(false));
        }
    };

    const user = useSelector((state: RootState) => state.user.user);

    const handleSubmit = async (data: any) => {
        const body: ICreateTimeOffRequest | IUpdateTimeoffRequest = {
            start_date: DateTimeUtil.formatDateToMySQL(selectedDates[0]),
            end_date: DateTimeUtil.formatDateToMySQL(selectedDates[selectedDates.length - 1]),
            ...data,
        };

        if (user.os_emp_account_id) {
            body.employee_id = user.os_emp_account_id;
        } else if (user.id) {
            body.employee_id = user.id.toString();
        }

        if (isUpdateTimeoffMode) {
            (body as IUpdateTimeoffRequest).time_off_request_id = timeoffToUpdate?.time_off_request_id!;
            await dispatch(updateTimeoffRequest(body as IUpdateTimeoffRequest));
        } else {
            await dispatch(createTimeOffRequest(body));
        }
        navigate("/mobile-dashboard");
    };

    return (
        <div className="max-w-lg mx-auto px-4 bg-white">
            {step === 1 && (
                <DatePickerStep
                    selectedDates={selectedDates}
                    setSelectedDates={setSelectedDates}
                    onNext={handleNextStep}
                    isUpdateMode={isUpdateTimeoffMode}
                    timeoffToUpdate={timeoffToUpdate}
                />
            )}
            {step === 2 && (
                <FormStep
                    formData={formData}
                    setFormData={setFormData}
                    selectedDates={selectedDates}
                    onBack={handlePreviousStep}
                    onSubmit={handleSubmit}
                    isUpdateMode={isUpdateTimeoffMode}
                    timeoffToUpdate={timeoffToUpdate}
                />
            )}
        </div>
    );
};

export default TimeOffRequest;
